import React from 'react';
import ReactDOM from 'react-dom/client';
import { FluentProvider, webLightTheme } from '@fluentui/react-components';
import { QueryClientProvider } from '@tanstack/react-query';
import { PublicClientApplication, EventType } from '@azure/msal-browser';
import { MsalProvider } from '@azure/msal-react';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { msalConfig } from './config/authConfig';
import { queryClient } from './queryClient';
import { setMsalInstance } from './auth/msalInstance';
import { renderBootError } from './bootError';
import { installBuildStampGlobal } from './buildInfo';
import { rememberLoginHint } from './services/silentSignIn';
import './i18n';
import './index.css';
import './fleet/uploadControl.css';

/**
 * ⚠ Constructed inside a function, not at module scope, and deliberately so.
 *
 * `new PublicClientApplication(...)` THROWS when its config is malformed — most
 * cheaply when `clientId` is undefined, which is what an unset `VITE_*` inlines
 * to. At module scope that throw escapes before any code can react to it: no
 * React root, no error boundary (none is mounted yet), no UI. The page stays
 * white and the only evidence is a console trace.
 *
 * That is not hypothetical — it is what shipped on 2026-07-31. Deferring
 * construction into `initializeMsal` puts it inside a try/catch that can render
 * something a human can act on.
 */
/**
 * ⚠ **No longer exported.** `services/conversionsApi.ts` used to import it from here, which made
 * the API layer and the entry point MUTUALLY RECURSIVE (`conversionsApi → main → App →
 * ClientPage → conversionsApi`) and meant importing the API started the UI. It now reads the
 * instance from `auth/msalInstance.ts`, which this function populates. BL-146.
 */
let msalInstance: PublicClientApplication;

async function initializeMsal() {
  msalInstance = new PublicClientApplication(msalConfig);
  setMsalInstance(msalInstance);
  await msalInstance.initialize();

  const response = await msalInstance.handleRedirectPromise();
  if (response) {
    msalInstance.setActiveAccount(response.account);
    rememberLoginHint(response.account); // CNV-003: the next silent attempt can name this person
  } else {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length > 0) {
      msalInstance.setActiveAccount(accounts[0]);
    }
  }

  msalInstance.addEventCallback((event) => {
    if (event.eventType === EventType.LOGIN_SUCCESS && event.payload) {
      const payload = event.payload as { account: unknown };
      // @ts-expect-error MSAL payload shape at runtime
      msalInstance.setActiveAccount(payload.account);
      // @ts-expect-error MSAL payload shape at runtime
      rememberLoginHint(payload.account);
    }
  });

  ReactDOM.createRoot(document.getElementById('root')!).render(
    <React.StrictMode>
      <MsalProvider instance={msalInstance}>
        <QueryClientProvider client={queryClient}>
          <FluentProvider theme={webLightTheme}>
            <BrowserRouter>
              <App />
            </BrowserRouter>
          </FluentProvider>
        </QueryClientProvider>
      </MsalProvider>
    </React.StrictMode>
  );
}

// Publish the build stamp on `window.__PTC_BUILD__` BEFORE anything that can throw, so
// "which commit is this?" is answerable from a browser console even on a page that failed
// to boot — which is precisely the state the 2026-07-31 white screen left us in, unable to
// identify the bundle or roll back to the previous one.
//
// ⚠ This call is also the ONLY thing referencing BUILD_STAMP in this app. `define`
// substitutes identifiers that appear in the source, and a constant nothing reads is
// dropped by tree-shaking — so removing this line does not just lose the console handle,
// it removes the stamp from the shipped bundle entirely. See src/buildInfo.ts.
installBuildStampGlobal();

// `.catch(console.error)` was the whole failure handler here, and it renders
// NOTHING — a boot failure produced a white page whose only trace was in the
// console. Any failure on this path now puts a legible, reportable screen in
// front of the user instead.
initializeMsal().catch(renderBootError);
